"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState, useEffect } from "react";

import { IconRenderer } from "./IconRenderer";

export interface ProcessStep {
  topTitle?: string;
  bottomTitle?: string;
  description?: string;
  iconName?: string;
  imageUrl?: string;
}

export interface ProcessSectionProps {
  heading?: string;
  introText?: string;
  clarifyingSentence?: string;
  buttonText?: string;
  buttonLink?: string;
  steps?: ProcessStep[];
}

function renderIcon(iconName?: string) {
  if (!iconName || iconName === "none") {
    return null;
  }

  return <IconRenderer name={iconName} className="size-8 text-white" />;
}

/**
 * Process Section - Interactive design with slide-up animation
 * Background: #B0732B, Inner box: #9E6625
 */
export default function ProcessSection({
  heading = "How It Works",
  introText = "A Medical Evaluation Comes First",
  clarifyingSentence = "This process helps ensure that any recommended treatment is appropriate, safe, and realistic for your condition.",
  buttonText = "Get started",
  buttonLink,
  steps,
}: ProcessSectionProps) {
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayStep, setDisplayStep] = useState(0);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const processSteps =
    steps && steps.length > 0
      ? steps
      : [
          {
            topTitle: "First Step",
            bottomTitle: "Medical Evaluation",
            description:
              "A qualified doctor reviews hair loss history, medical background, and prior treatments.",
            iconName: "FaFileMedical",
          },
          {
            topTitle: "Second Step",
            bottomTitle: "Hair & Scalp Assessment",
            description:
              "Detailed examination of scalp condition and donor areas to guide suitability.",
            iconName: "FaSearch",
          },
          {
            topTitle: "Third Step",
            bottomTitle: "Suitability Discussion",
            description:
              "Clear explanation of available options, limitations, and expectations.",
            iconName: "FaComments",
          },
          {
            topTitle: "Fourth Step",
            bottomTitle: "Next-Step Planning",
            description:
              "If suitable, treatment planning and guidance are discussed with transparency.",
            iconName: "FaRoute",
          },
        ];

  const handleGetStarted = () => {
    setIsAutoRotating(true);
  };

  // Auto-start animation on component mount
  useEffect(() => {
    // Start auto-rotation automatically after a short delay
    const startTimer = setTimeout(() => {
      setIsAutoRotating(true);
    }, 1000); // Start after 1 second

    return () => clearTimeout(startTimer);
  }, []);

  // Auto-rotate through steps
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (isAutoRotating && processSteps.length > 0 && !isTransitioning) {
      intervalRef.current = setInterval(() => {
        setIsTransitioning(true);
        // After animation completes, update both activeStep and displayStep
        setTimeout(() => {
          setActiveStep((prev) => {
            const nextStep = (prev + 1) % processSteps.length;
            setDisplayStep(nextStep);
            setIsTransitioning(false);
            return nextStep;
          });
        }, 2500); // Slower transition duration (2.5 seconds)
      }, 5000); // Change step every 5 seconds (slower)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [isAutoRotating, isTransitioning, processSteps.length]);

  const currentStep = processSteps[displayStep];
  const nextStepIndex = (activeStep + 1) % processSteps.length;
  const nextStep = processSteps[nextStepIndex];

  return (
    <section className="w-full py-8 md:py-12">
      <div
        className="mx-auto w-full max-w-screen-2xl rounded-3xl p-6 md:p-8 lg:p-12"
        style={{
          backgroundColor: "#9E6625",
          backgroundImage: `
            radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px),
            radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px, 40px 40px",
          backgroundPosition: "0 0, 10px 10px",
        }}
      >
        {/* Header Section */}
        {(heading || introText || clarifyingSentence) && (
          <div className="mb-12 text-center">
            {heading && (
              <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">
                {heading}
              </h2>
            )}
            {introText && (
              <p className="mb-3 text-lg italic text-white/90 md:text-xl">
                {introText}
              </p>
            )}
            {clarifyingSentence && (
              <p className="mx-auto max-w-3xl text-base leading-relaxed text-white/80 md:text-lg">
                {clarifyingSentence}
              </p>
            )}
          </div>
        )}

        {/* Main Content Box */}
        <div className="relative overflow-hidden">
          {/* Single Step Display - Shows only active step */}
          {currentStep && (
            <article className="flex flex-col items-center">
              {/* Inner Box with Light Brown Background */}
              <div
                className="relative w-full max-w-lg rounded-3xl p-6 md:p-8 lg:p-12"
                style={{ backgroundColor: "#B8865A" }}
              >
                {/* Step Image Container with Slide Animation */}
                {currentStep.imageUrl && (
                  <div className="relative w-full">
                    {/* Top Title - Above Image, Left Aligned */}
                    <div className="mb-2 text-left">
                      {currentStep.topTitle && (
                        <p
                          key={`top-${displayStep}`}
                          className="fade-in-text"
                          style={{
                            fontSize: "30px",
                            lineHeight: "1.2",
                            fontWeight: "normal",
                            textTransform: "none",
                            letterSpacing: "-0.02em",
                            color: "#FFFFFF",
                          }}
                        >
                          {currentStep.topTitle}
                        </p>
                      )}
                    </div>

                    {/* Image Slider Container */}
                    <div className="relative aspect-video w-full overflow-hidden rounded-xl">
                      {/* Current Step Image - Slides up */}
                      <div
                        className="absolute inset-0"
                        style={{
                          transform: `translateY(${isTransitioning ? "-100%" : "0%"})`,
                          transition: isTransitioning
                            ? "transform 2500ms ease-in-out"
                            : "none",
                          zIndex: 2,
                        }}
                      >
                        <div className="relative aspect-video w-full">
                          <Image
                            src={currentStep.imageUrl}
                            alt={
                              currentStep.bottomTitle ||
                              `Step ${activeStep + 1}`
                            }
                            fill
                            className="object-cover rounded-xl"
                          />
                        </div>
                      </div>

                      {/* Next Step Image - Appears from bottom and stays */}
                      {nextStep?.imageUrl && (
                        <div
                          className="absolute inset-0"
                          style={{
                            transform: `translateY(${isTransitioning ? "0%" : "100%"})`,
                            transition: isTransitioning
                              ? "transform 2500ms ease-in-out"
                              : "none",
                            zIndex: 1,
                          }}
                        >
                          <div className="relative aspect-video w-full">
                            <Image
                              src={nextStep.imageUrl}
                              alt={
                                nextStep.bottomTitle ||
                                `Step ${nextStepIndex + 1}`
                              }
                              fill
                              className="object-cover rounded-xl"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Bottom Title - Below Image, Centered */}
                    <div className="mt-2 text-center">
                      {currentStep.bottomTitle && (
                        <h3
                          key={`bottom-${displayStep}`}
                          className="fade-in-text"
                          style={{
                            fontSize: "45px",
                            lineHeight: "1.2",
                            fontWeight: "normal",
                            textTransform: "none",
                            letterSpacing: "-0.02em",
                            color: "#FFC671",
                          }}
                        >
                          {currentStep.bottomTitle}
                        </h3>
                      )}
                    </div>

                    {/* Step Description - Optional */}
                    {currentStep.description && (
                      <p
                        key={`desc-${displayStep}`}
                        className="mt-4 text-center leading-relaxed text-white/90 fade-in-text"
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {currentStep.description}
                      </p>
                    )}

                    {/* Get Started Button - Inside the box */}
                    <div className="mt-6 flex justify-center">
                      {buttonLink ? (
                        <Link
                          href={buttonLink}
                          className="inline-block rounded-full px-8 py-3 text-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
                          style={{ backgroundColor: "#9E6625" }}
                        >
                          {buttonText}
                        </Link>
                      ) : (
                        <button
                          onClick={handleGetStarted}
                          className="rounded-full px-8 py-3 text-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
                          style={{ backgroundColor: "#9E6625" }}
                        >
                          {buttonText}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {!currentStep.imageUrl && (
                  <div className="relative w-full">
                    {currentStep.topTitle && (
                      <div className="mb-2 text-left">
                        <p
                          style={{
                            fontSize: "30px",
                            lineHeight: "1.2",
                            fontWeight: "normal",
                            textTransform: "none",
                            letterSpacing: "-0.02em",
                            color: "#FFFFFF",
                          }}
                        >
                          {currentStep.topTitle}
                        </p>
                      </div>
                    )}
                    {currentStep.bottomTitle && (
                      <div className="mt-2 text-center">
                        <h3
                          style={{
                            fontSize: "45px",
                            lineHeight: "1.2",
                            fontWeight: "normal",
                            textTransform: "none",
                            letterSpacing: "-0.02em",
                            color: "#FFC671",
                          }}
                        >
                          {currentStep.bottomTitle}
                        </h3>
                      </div>
                    )}
                    {/* Step Description - Optional */}
                    {currentStep.description && (
                      <p
                        className="mt-4 text-center leading-relaxed text-white/90"
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {currentStep.description}
                      </p>
                    )}
                    {/* Get Started Button - Inside the box */}
                    <div className="mt-6 flex justify-center">
                      {buttonLink ? (
                        <Link
                          href={buttonLink}
                          className="inline-block rounded-full px-8 py-3 text-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
                          style={{ backgroundColor: "#9E6625" }}
                        >
                          {buttonText}
                        </Link>
                      ) : (
                        <button
                          onClick={handleGetStarted}
                          className="rounded-full px-8 py-3 text-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
                          style={{ backgroundColor: "#9E6625" }}
                        >
                          {buttonText}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </article>
          )}
        </div>

        {/* Step Indicator Circles - Always visible */}
        <div className="mt-6 flex justify-center gap-3">
          {processSteps.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                // Immediately stop auto-rotation and clear interval
                setIsAutoRotating(false);
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                }

                // If clicking a different step, animate to it
                if (index !== displayStep) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setActiveStep(index);
                    setDisplayStep(index);
                    setIsTransitioning(false);
                  }, 2500); // Full transition duration
                } else {
                  // Already on this step, just ensure states are synced
                  setActiveStep(index);
                  setDisplayStep(index);
                }
              }}
              className={`flex size-10 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                displayStep === index
                  ? "bg-white text-[#9E6625] scale-110 shadow-lg"
                  : "bg-white/30 text-white hover:bg-white/50"
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
